import { useEffect, useMemo, useState } from "react";
import api from "../services/axiosInstance";
import { backendPort } from "../components/Fungsiku";

function formatTanggal(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy}`;
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function getEventBadgeClass(eventName) {
  const e = String(eventName || "").toLowerCase();
  if (e.includes("initial") || e.includes("migrasi")) return "is-ok";
  if (e.includes("tambal") || e.includes("rotasi") || e.includes("storing")) return "is-info";
  if (e.includes("mutasi")) return "is-warning";
  return "is-neutral";
}

function isInitialOrMigrasiEvent(eventName) {
  const e = String(eventName || "").trim().toLowerCase();
  return e.startsWith("initial") || e.startsWith("migrasi");
}

function toSentenceCase(value) {
  const raw = String(value || "").trim().replace(/_/g, " ");
  if (!raw) return "";
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

function normalizeHistoryInfoValue(rawValue, keyName, maps = {}) {
  const keyLower = String(keyName || "").trim().toLowerCase();
  const cabangByKode = maps?.cabangByKode || new Map();
  const mtById = maps?.mtById || new Map();
  const seriByIdBan = maps?.seriByIdBan || new Map();

  const mapCabang = (value) => {
    const raw = String(value || "").trim();
    if (!/^C-/i.test(raw)) return raw;
    return cabangByKode.get(raw.toUpperCase()) || raw;
  };
  const mapMt = (value) => {
    const raw = String(value || "").trim();
    if (!/^K-/i.test(raw)) return raw;
    return mtById.get(raw.toUpperCase()) || raw;
  };
  const mapIdBan = (value) => {
    const idNum = toNumber(value, 0);
    if (!idNum) return String(value || "").trim();
    return seriByIdBan.get(String(idNum)) || String(value || "").trim();
  };

  if (Array.isArray(rawValue)) {
    return rawValue.map((item) => normalizeHistoryInfoValue(item, keyName, maps)).filter(Boolean).join(", ");
  }
  if (rawValue && typeof rawValue === "object") {
    return Object.entries(rawValue)
      .map(([k, v]) => {
        const val = normalizeHistoryInfoValue(v, k, maps);
        return val ? `${toSentenceCase(k)}: ${val}` : "";
      })
      .filter(Boolean)
      .join(", ");
  }

  const text = String(rawValue ?? "").trim();
  if (!text) return "";

  if (keyLower === "dokumen") {
    const [nomor, tanggal] = text.split("#");
    if (nomor && tanggal) return `${String(nomor).trim()} tgl. ${String(tanggal).trim()}`;
  }

  if (keyLower === "id_ban" || keyLower === "idban") {
    return mapIdBan(text);
  }
  if (keyLower === "kode_cabang" || keyLower === "cabang" || keyLower === "penerima" || keyLower === "pengirim") {
    return mapCabang(text);
  }
  if (keyLower === "id_mt" || keyLower === "mt") {
    return mapMt(text);
  }

  if (/^C-/i.test(text)) return mapCabang(text);
  if (/^K-/i.test(text)) return mapMt(text);
  return text;
}

function buildInfoFromReff(reffRaw, maps = {}) {
  let reffObj = {};
  if (!reffRaw) return "-";
  if (typeof reffRaw === "string") {
    try {
      reffObj = JSON.parse(reffRaw || "{}") || {};
    } catch {
      return "-";
    }
  } else if (typeof reffRaw === "object") {
    reffObj = reffRaw || {};
  }
  if (!reffObj || typeof reffObj !== "object" || Array.isArray(reffObj)) return "-";

  const parts = Object.entries(reffObj)
    .filter(([key, value]) => {
      const k = String(key || "").trim().toLowerCase();
      if (k === "nomor") return false;
      const val = normalizeHistoryInfoValue(value, key, maps);
      return Boolean(val);
    })
    .map(([key, value]) => `${toSentenceCase(key)}: ${normalizeHistoryInfoValue(value, key, maps)}`)
    .filter(Boolean);
  return parts.length ? parts.join(" | ") : "-";
}

export function PageKonten28_BanShow({
  globalOpsi,
  idBan,
  selectedPosisi = "",
  onClose = null,
  embedded = false,
  reloadToken = 0,
}) {
  const [historyRows, setHistoryRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [profilBan, setProfilBan] = useState(null);

  const idBanNum = toNumber(idBan, 0);
  const opsiCabang = Array.isArray(globalOpsi?.opsiCabang) ? globalOpsi.opsiCabang : [];
  const opsiMT = Array.isArray(globalOpsi?.opsiMT) ? globalOpsi.opsiMT : [];
  const opsiBan = Array.isArray(globalOpsi?.opsiBan) ? globalOpsi.opsiBan : [];
  const historyInfoMaps = useMemo(() => {
    const cabangByKode = new Map();
    opsiCabang.forEach((x) => {
      const kode = String(x?.kode_cabang || "").trim().toUpperCase();
      const nama = String(x?.nama_cabang || "").trim();
      if (kode && nama) cabangByKode.set(kode, nama);
    });
    const mtById = new Map();
    opsiMT.forEach((x) => {
      const idMt = String(x?.id_mt || "").trim().toUpperCase();
      const nopol = String(x?.nopol || "").trim();
      if (idMt && nopol) mtById.set(idMt, nopol);
    });
    const seriByIdBan = new Map();
    opsiBan.forEach((x) => {
      const id = String(toNumber(x?.id_ban, 0));
      const seri = String(x?.seri_ban || "").trim();
      if (id !== "0" && seri) seriByIdBan.set(id, seri);
    });
    return { cabangByKode, mtById, seriByIdBan };
  }, [opsiCabang, opsiMT, opsiBan]);

  const normalizePlaceLabel = (rawPlace) => {
    const raw = String(rawPlace || "").trim();
    if (!raw) return "-";

    const gudangMatch = raw.match(/^gudang#([a-z0-9-]+)$/i);
    if (gudangMatch) {
      const kodeCabang = String(gudangMatch[1] || "").trim();
      const cabangRow = opsiCabang.find(
        (x) => String(x?.kode_cabang || "").trim().toLowerCase() === kodeCabang.toLowerCase()
      );
      return `Gudang ${String(cabangRow?.nama_cabang || kodeCabang).trim()}`;
    }

    const mtPlaceMatch = raw.match(/^([k]-[a-z0-9-]+)#([a-z0-9-]+)$/i);
    if (mtPlaceMatch) {
      const idMt = String(mtPlaceMatch[1] || "").trim();
      const posisi = String(mtPlaceMatch[2] || "").trim().toUpperCase();
      const mtRow = opsiMT.find(
        (x) => String(x?.id_mt || "").trim().toLowerCase() === idMt.toLowerCase()
      );
      const nopol = String(mtRow?.nopol || idMt).trim();
      return `${nopol}-${posisi}`;
    }

    const pabrikMatch = raw.match(/^pabrik#(.+)$/i);
    if (pabrikMatch) {
      return `Pabrik ${String(pabrikMatch[1] || "").trim()}`;
    }

    return raw;
  };

  const normalizePlaceInText = (text) => {
    const raw = String(text || "").trim();
    if (!raw || raw === "-") return "-";
    return raw
      .replace(/\bgudang#([a-z0-9-]+)\b/gi, (m) => normalizePlaceLabel(m))
      .replace(/\b[k]-[a-z0-9-]+#[a-z0-9-]+\b/gi, (m) => normalizePlaceLabel(m))
      .replace(/\bpabrik#([^|,\n\r]+)/gi, (m) => normalizePlaceLabel(m));
  };

  useEffect(() => {
    let ignore = false;
    const loadProfilBan = async () => {
      if (!idBanNum) {
        setProfilBan(null);
        return;
      }
      try {
        const { data } = await api.get(`${backendPort}/api/main/load_profil_ban/${idBanNum}`);
        if (ignore) return;
        setProfilBan(data && typeof data === "object" ? data : null);
      } catch {
        if (!ignore) setProfilBan(null);
      }
    };
    loadProfilBan();
    return () => {
      ignore = true;
    };
  }, [idBanNum, reloadToken]);

  useEffect(() => {
    let ignore = false;
    const loadHistory = async () => {
      if (!idBanNum) {
        setHistoryRows([]);
        setErrorText("ID ban belum tersedia.");
        return;
      }
      setLoading(true);
      setErrorText("");
      try {
        const { data } = await api.post(`${backendPort}/api/main/load_ban_event_history_by_ban`, {
          kodeBan: [idBanNum],
        });
        if (ignore) return;
        const rows = Array.isArray(data) ? data : [];
        const filtered = rows
          .filter((x) => toNumber(x?.id_ban, 0) === idBanNum)
          .sort((a, b) => {
            const dA = new Date(a?.tanggal || 0).getTime();
            const dB = new Date(b?.tanggal || 0).getTime();
            if (dA !== dB) return dB - dA;
            return toNumber(b?.id_event, 0) - toNumber(a?.id_event, 0);
          });
        setHistoryRows(filtered);
      } catch (err) {
        if (ignore) return;
        setHistoryRows([]);
        setErrorText(err?.response?.data?.message || "Gagal memuat histori ban.");
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    loadHistory();
    return () => {
      ignore = true;
    };
  }, [idBanNum, reloadToken]);

  const latestEvent = historyRows[0] || null;
  const initialEvent = useMemo(() => {
    const candidates = historyRows.filter((x) => isInitialOrMigrasiEvent(x?.event));
    if (candidates.length === 0) return null;

    return candidates.reduce((earliest, row) => {
      const tEarliest = new Date(earliest?.tanggal || 0).getTime();
      const tRow = new Date(row?.tanggal || 0).getTime();
      if (Number.isNaN(tEarliest)) return row;
      if (Number.isNaN(tRow)) return earliest;
      if (tRow < tEarliest) return row;
      if (tRow > tEarliest) return earliest;
      return toNumber(row?.id_event, 0) < toNumber(earliest?.id_event, 0) ? row : earliest;
    }, candidates[0]);
  }, [historyRows]);

  const kodeCabangKelola = String(profilBan?.kode_cabang || "").trim();
  const namaCabangKelola =
    (kodeCabangKelola
      ? (opsiCabang.find((x) => String(x?.kode_cabang || "").trim() === kodeCabangKelola)?.nama_cabang || "-")
      : "-");

  const seriBanDisplay =
    String(profilBan?.seri_ban || "").trim() ||
    "-";
  const merkDisplay =
    String(profilBan?.merk || "").trim() ||
    "-";
  const ukuranDisplay =
    String(profilBan?.ukuran || "").trim() ||
    "-";
  const kmtTerakhirDisplay =
    profilBan?.kmt
    ?? "-";
  const tdTerakhirDisplay =
    String(
      profilBan?.td
      || ""
    ).trim() || "-";

  const ringkasanRows = [
    { label: "Cabang Pengelola", value: namaCabangKelola, hint: kodeCabangKelola || "-" },
    { label: "ID Ban", value: idBanNum || "-" },
    { label: "No. Seri", value: seriBanDisplay },
    { label: "Merk", value: merkDisplay },
    { label: "Ukuran", value: ukuranDisplay },
    { label: "Status", value: String(profilBan?.status || "").trim() || "-" },
    { label: "Tanggal Initial", value: formatTanggal(profilBan?.tanggal_initial) },
    { label: "KMT Terakhir", value: kmtTerakhirDisplay },
    { label: "TD Terakhir", value: tdTerakhirDisplay },
  ];

  return (
    <div className={embedded ? "page28-ban-shell embedded" : "page28-ban-shell"}>
      <div className="page28-ban-head">
        <div>
          <h5 className="mb-1">Detail Ban</h5>
          <small className="text-muted">
            {selectedPosisi ? `Posisi: ${selectedPosisi} | ` : ""}
            {idBanNum ? `ID: ${idBanNum}` : "Pilih ban terlebih dahulu"}
          </small>
        </div>
        {typeof onClose === "function" && (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary page16-panel-close"
            onClick={onClose}
            aria-label="Tutup"
          >
            <i className="fa fa-times" aria-hidden="true" />
          </button>
        )}
      </div>

      {errorText ? <div className="alert alert-warning py-2 mb-3">{errorText}</div> : null}

      <div className="row g-3">
        <div className="col-12 col-lg-5">
          <div className="card page28-ban-card h-100">
            <div className="card-body">
              <h6 className="page28-ban-title">Profil Ban</h6>
              <div className="page28-ban-kv">
                {ringkasanRows.map((row) => (
                  <div key={`kv-${row.label}`} className="page28-ban-kv-row">
                    <span className="key">{row.label}</span>
                    <span className="value">
                      {row.value}
                      {row.hint ? <small className="hint">{` (${row.hint})`}</small> : null}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-7">
          <div className="card page28-ban-card h-100">
            <div className="card-body">
              <h6 className="page28-ban-title">Histori Ban</h6>
              <div className="page28-ban-history">
                {loading && <div className="text-muted small">Memuat histori ban...</div>}
                {!loading && historyRows.length === 0 && (
                  <div className="text-muted small">Belum ada histori ban.</div>
                )}
                {!loading &&
                  historyRows.map((row) => (
                    <div key={`hist-ban-${row.id_event}`} className="page28-ban-history-item">
                      <div className="meta">
                        <span className="tgl">{formatTanggal(row.tanggal)}</span>
                        <span className={`badge-event ${getEventBadgeClass(row.event)}`}>
                          {row.event || "-"}
                        </span>
                      </div>
                      <div className="row g-1 small mt-1">
                        <div className="col-6"><strong>KMT:</strong> {row.kmt ?? "-"}</div>
                        <div className="col-6"><strong>TD:</strong> {row.td || "-"}</div>
                        <div className="col-6"><strong>Status:</strong> {row.status || "-"}</div>
                        <div className="col-6"><strong>Place:</strong> {normalizePlaceLabel(row.place)}</div>
                      </div>
                      <div className="small mt-2">{normalizePlaceInText(buildInfoFromReff(row?.reff, historyInfoMaps))}</div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ModalBanShow({
  open,
  onClose,
  globalOpsi,
  idBan,
  selectedPosisi = "",
  reloadToken = 0,
}) {
  if (!open) return null;
  return (
    <div className="page16-modal-backdrop" role="dialog" aria-modal="true">
      <div className="page16-modal-card" style={{ width: "min(1120px, 96vw)" }}>
        <PageKonten28_BanShow
          globalOpsi={globalOpsi}
          idBan={idBan}
          selectedPosisi={selectedPosisi}
          onClose={onClose}
          embedded
          reloadToken={reloadToken}
        />
      </div>
    </div>
  );
}
